/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * MCP server activity target type. Only the server-backed kinds this endpoint reports are valid, unlike the broader tool-usage target types.
 */
export const TargetType = {
  HostedMcpServer: "hosted_mcp_server",
  TunneledMcpServer: "tunneled_mcp_server",
  MetaMcpServer: "meta_mcp_server",
} as const;
/**
 * MCP server activity target type. Only the server-backed kinds this endpoint reports are valid, unlike the broader tool-usage target types.
 */
export type TargetType = ClosedEnum<typeof TargetType>;

/**
 * Tool-call activity for one MCP server, keyed by the same target identifier used elsewhere (toolset slug for hosted servers, MCP server slug for tunneled/remote servers, meta MCP server id for gateways)
 */
export type McpServerActivity = {
  /**
   * ISO 8601 timestamp of the most recent tool call
   */
  lastToolCallAt?: Date | undefined;
  /**
   * Number of tool calls observed inside the recent-activity window
   */
  recentToolCalls: number;
  /**
   * Stable target identifier: toolset slug for hosted servers, MCP server slug for tunneled/remote servers, meta MCP server id for gateways
   */
  targetId: string;
  /**
   * User-facing label for the target. Gateway rows carry the gateway name, falling back to the meta MCP server id when the gateway no longer exists
   */
  targetLabel: string;
  /**
   * MCP server activity target type. Only the server-backed kinds this endpoint reports are valid, unlike the broader tool-usage target types.
   */
  targetType: TargetType;
  /**
   * Number of tool calls observed across the whole lookback window
   */
  totalToolCalls: number;
};

/** @internal */
export const TargetType$inboundSchema: z.ZodMiniEnum<typeof TargetType> = z
  .enum(TargetType);

/** @internal */
export const McpServerActivity$inboundSchema: z.ZodMiniType<
  McpServerActivity,
  unknown
> = z.pipe(
  z.object({
    last_tool_call_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    recent_tool_calls: z.int(),
    target_id: z.string(),
    target_label: z.string(),
    target_type: TargetType$inboundSchema,
    total_tool_calls: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "last_tool_call_at": "lastToolCallAt",
      "recent_tool_calls": "recentToolCalls",
      "target_id": "targetId",
      "target_label": "targetLabel",
      "target_type": "targetType",
      "total_tool_calls": "totalToolCalls",
    });
  }),
);

export function mcpServerActivityFromJSON(
  jsonString: string,
): SafeParseResult<McpServerActivity, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => McpServerActivity$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'McpServerActivity' from JSON`,
  );
}
